import React, { ReactNode } from 'react';
import { Box, Chip, Collapse, IconButton, Paper, Typography } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { SrtSectionConfig } from '../types';
import { srtStyles } from '../styles';

interface FormSRTSectionCardProps {
    section: SrtSectionConfig;
    displayNumber: number;
    isComplete: boolean;
    showStatusChip?: boolean;
    isOpen: boolean;
    onToggle: (sectionId: number) => void;
    children: ReactNode;
    disabled?: boolean;
}

const FormSRTSectionCard = React.memo(({
    section,
    displayNumber,
    isComplete,
    showStatusChip = true,
    isOpen,
    onToggle,
    children,
    disabled = false
}: FormSRTSectionCardProps) => {
    const { t } = useTranslation('siniestrosRoam');
    const handleToggle = () => {
        if (disabled) return;
        onToggle(section.id);
    };

    return (
        <Paper
            elevation={0}
            sx={disabled ? { ...srtStyles.sectionCard, opacity: 0.5 } : srtStyles.sectionCard}
        >
            <Box
                sx={srtStyles.sectionHeader(isOpen)}
                onClick={handleToggle}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(event) => {
                    if (disabled) return;
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleToggle();
                    }
                }}
                style={disabled ? { cursor: 'default' } : undefined}
            >
                <Box sx={srtStyles.sectionTitleWrapper}>
                    <Box sx={srtStyles.sectionNumber}>
                        {String(displayNumber).padStart(2, '0')}
                    </Box>
                    <Typography variant="body2" sx={srtStyles.sectionTitle}>
                        {section.title}
                    </Typography>
                </Box>

                <Box sx={srtStyles.sectionActions}>
                    {showStatusChip && (
                        <Chip
                            size="small"
                            label={
                                isComplete
                                    ? t('detail.srtForm.status.complete')
                                    : t('detail.srtForm.status.incomplete')
                            }
                            sx={srtStyles.statusChip(isComplete)}
                        />
                    )}
                    <IconButton
                        size="small"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleToggle();
                        }}
                    >
                        {isOpen ? (
                            <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                            <KeyboardArrowDownIcon fontSize="small" />
                        )}
                    </IconButton>
                </Box>
            </Box>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <Box sx={srtStyles.sectionBody}>{isOpen ? children : null}</Box>
            </Collapse>
        </Paper>
    );
});

export default FormSRTSectionCard;
